import { fetchRequestWithTypes } from './index.js';

/**
 * Orders px rollover client. Paths and JSON field names match the mobile
 * Rollovers API. 4xx bodies stay the shared px envelope
 * `{ error: { code, message, details } }`; fetch forwards that JSON on the
 * errored action.
 */

export const GET_ROLLOVERS_REQUEST = 'GET_ROLLOVERS_REQUEST';
export const GET_ROLLOVERS_SUCCESS = 'GET_ROLLOVERS_SUCCESS';
export const GET_ROLLOVERS_ERRORED = 'GET_ROLLOVERS_ERRORED';

export const POST_ROLLOVER_WEBVIEW_URL_REQUEST = 'POST_ROLLOVER_WEBVIEW_URL_REQUEST';
export const POST_ROLLOVER_WEBVIEW_URL_SUCCESS = 'POST_ROLLOVER_WEBVIEW_URL_SUCCESS';
export const POST_ROLLOVER_WEBVIEW_URL_ERRORED = 'POST_ROLLOVER_WEBVIEW_URL_ERRORED';

export const POST_ROLLOVER_EVENT_REQUEST = 'POST_ROLLOVER_EVENT_REQUEST';
export const POST_ROLLOVER_EVENT_SUCCESS = 'POST_ROLLOVER_EVENT_SUCCESS';
export const POST_ROLLOVER_EVENT_ERRORED = 'POST_ROLLOVER_EVENT_ERRORED';

export const ROLLOVER_LIST_PATH = '/apis/orders/v1/rollovers';
export const ROLLOVER_WEBVIEW_URL_PATH = '/apis/orders/v1/rollovers/webview-url';
export const ROLLOVER_EVENTS_PATH = '/apis/orders/v1/rollovers/events';

export const ROLLOVER_WEBVIEW_ROUTE = {
	NEW_ROLLOVER: 'ROLLOVER_WEBVIEW_ROUTE_NEW_ROLLOVER',
	ROLLOVER_HISTORY: 'ROLLOVER_WEBVIEW_ROUTE_ROLLOVER_HISTORY',
} as const;

export type RolloverWebviewRoute =
	(typeof ROLLOVER_WEBVIEW_ROUTE)[keyof typeof ROLLOVER_WEBVIEW_ROUTE];

export const ROLLOVER_EVENT_TYPE = {
	UNSPECIFIED: 'ROLLOVER_EVENT_TYPE_UNSPECIFIED',
	ROLLOVER_CREATED: 'ROLLOVER_EVENT_TYPE_ROLLOVER_CREATED',
	ROLLOVER_SUBMITTED: 'ROLLOVER_EVENT_TYPE_ROLLOVER_SUBMITTED',
	CREATE_TRAD_IRA: 'ROLLOVER_EVENT_TYPE_CREATE_TRAD_IRA',
	CREATE_ROTH_IRA: 'ROLLOVER_EVENT_TYPE_CREATE_ROTH_IRA',
	EXIT: 'ROLLOVER_EVENT_TYPE_EXIT',
	ERROR: 'ROLLOVER_EVENT_TYPE_ERROR',
	ROLLOVER_CANCELED: 'ROLLOVER_EVENT_TYPE_ROLLOVER_CANCELED',
} as const;

export type RolloverEventType = (typeof ROLLOVER_EVENT_TYPE)[keyof typeof ROLLOVER_EVENT_TYPE];

export const ROLLOVER_STATUS = {
	UNSPECIFIED: 'ROLLOVER_STATUS_UNSPECIFIED',
	ROLLOVER_CREATED: 'ROLLOVER_STATUS_ROLLOVER_CREATED',
	INCOMPLETE: 'ROLLOVER_STATUS_INCOMPLETE',
	REQUIRES_ACTION: 'ROLLOVER_STATUS_REQUIRES_ACTION',
	PENDING_REVIEW: 'ROLLOVER_STATUS_PENDING_REVIEW',
	ROLLOVER_SUBMITTED: 'ROLLOVER_STATUS_ROLLOVER_SUBMITTED',
	PROCESSING: 'ROLLOVER_STATUS_PROCESSING',
	COMPLETE: 'ROLLOVER_STATUS_COMPLETE',
	CANCELED: 'ROLLOVER_STATUS_CANCELED',
	INELIGIBLE: 'ROLLOVER_STATUS_INELIGIBLE',
} as const;

export type RolloverStatus = (typeof ROLLOVER_STATUS)[keyof typeof ROLLOVER_STATUS];

const TERMINAL_ROLLOVER_STATUSES = new Set<string>([
	ROLLOVER_STATUS.COMPLETE,
	ROLLOVER_STATUS.CANCELED,
	ROLLOVER_STATUS.INELIGIBLE,
	ROLLOVER_STATUS.UNSPECIFIED,
]);

const ACTION_REQUIRED_ROLLOVER_STATUSES = new Set<string>([
	ROLLOVER_STATUS.REQUIRES_ACTION,
	ROLLOVER_STATUS.PENDING_REVIEW,
]);

export type RolloverSummary = {
	transactionId: string;
	status: string;
};

export type GetRolloverWebviewUrlRequest = {
	transactionId?: string;
	route?: RolloverWebviewRoute;
};

export type CreateRolloverEventRequest = {
	eventType: RolloverEventType;
	transactionId?: string;
	errorMessage?: string;
};

/** Px 4xx envelope forwarded unchanged on the errored action. */
export type PxRolloverError = {
	error: {
		code: string;
		message: string;
		details?: {
			'@type': string;
			message?: string | { field: string; description: string }[];
		};
	};
};

export const getRollovers = () =>
	fetchRequestWithTypes({
		types: [GET_ROLLOVERS_REQUEST, GET_ROLLOVERS_SUCCESS, GET_ROLLOVERS_ERRORED],
		request: {
			method: 'GET',
			path: ROLLOVER_LIST_PATH,
		},
	});

export const getRolloverWebviewUrl = ({
	transactionId,
	route,
}: GetRolloverWebviewUrlRequest) =>
	fetchRequestWithTypes({
		types: [
			POST_ROLLOVER_WEBVIEW_URL_REQUEST,
			POST_ROLLOVER_WEBVIEW_URL_SUCCESS,
			POST_ROLLOVER_WEBVIEW_URL_ERRORED,
		],
		request: {
			method: 'POST',
			path: ROLLOVER_WEBVIEW_URL_PATH,
			body: omitEmpty({ transactionId, route }),
		},
	});

/**
 * Relays a Capitalize webview event. Callers ignore the result; a failed post
 * must not block the rollover UI.
 */
export const createRolloverEvent = ({
	eventType,
	transactionId,
	errorMessage,
}: CreateRolloverEventRequest) =>
	fetchRequestWithTypes({
		types: [POST_ROLLOVER_EVENT_REQUEST, POST_ROLLOVER_EVENT_SUCCESS, POST_ROLLOVER_EVENT_ERRORED],
		request: {
			method: 'POST',
			path: ROLLOVER_EVENTS_PATH,
			body: omitEmpty({ eventType, transactionId, errorMessage }),
		},
	});

/**
 * Prefers an action-required rollover, otherwise the first non-terminal one.
 * Returns null when every rollover is terminal.
 */
export function resolveInProgressRollover<T extends RolloverSummary>(
	rollovers: readonly T[]
): T | null {
	const nonTerminal = getInProgressRollovers(rollovers);
	return nonTerminal.find(isActionRequiredRollover) ?? nonTerminal[0] ?? null;
}

export function isActionRequiredRollover(rollover: RolloverSummary): boolean {
	return ACTION_REQUIRED_ROLLOVER_STATUSES.has(rollover.status);
}

export function getInProgressRollovers<T extends RolloverSummary>(
	rollovers: readonly T[]
): T[] {
	return rollovers.filter((rollover) => !TERMINAL_ROLLOVER_STATUSES.has(rollover.status));
}

function omitEmpty(body: Record<string, string | undefined>): Record<string, string> {
	return Object.fromEntries(
		Object.entries(body).filter((entry): entry is [string, string] => entry[1] != null && entry[1] !== '')
	);
}
